import { useState } from 'react';
import { EntityType, EntityTypeSchema, update } from '@/api/doc/entity-type';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateEntityType({ entityType }: { entityType: EntityType }) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedEntityType: EntityType) => update(updatedEntityType),
		onSuccess: () => {
			toast.success(`Success! The entity type has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({ queryKey: ['entity-types'] });
		},
		onError: () => {
			toast.error(`Error! The entity type could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<EntityType>({
		resolver: zodResolver(EntityTypeSchema),
		defaultValues: entityType,
		mode: 'all',
	});

	const deletedDescription: string = !entityType.deletedOn
		? 'Toggle on to delete this entity type.'
		: 'Toggle off to not delete this entity type.';

	const deletedLabel: string = !entityType.deletedOn ? 'Delete?' : 'Deleted';

	const onSubmit: SubmitHandler<EntityType> = (values: EntityType) =>
		mutate(values);

	form.watch();
	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Entity Type</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent className="overflow-scroll">
					<SheetHeader>
						<SheetTitle>Update Entity Type</SheetTitle>
					</SheetHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Entity Type Name</FormLabel>
										<FormControl>
											<Input {...field} placeholder="Entity type name" />
										</FormControl>
										<FormDescription>Name the entity type</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="deletedOn"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
										<div className="space-y-0.5">
											<FormLabel>{deletedLabel}</FormLabel>
											<FormDescription>{deletedDescription}</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={!!field.value}
												onCheckedChange={() =>
													form.setValue('deletedOn', field.value ? null : new Date())
												}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
